import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Incident, IncidentSeverity, IncidentStatus, IncidentSummary } from '@regigov/contracts';
import { incidentsApi } from '../api/selfservice';
import { useAuth } from '../auth/AuthContext';
import { isCerRole } from '../auth/roles';
import { DefinitionList, Field, ProblemAlert, StatusBadge, formatDateTime } from './ui';

type Mode = 'cer' | 'participant';
const STATUSES: IncidentStatus[] = ['open', 'acknowledged', 'investigating', 'resolved', 'closed'];
const SEVERITIES: IncidentSeverity[] = ['low', 'medium', 'high', 'critical'];

export function IncidentsPage({ mode, basePath }: { mode: Mode; basePath: string }) {
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const q = params.get('q') ?? '';
  const [draft, setDraft] = useState(q);
  const query = { status: (status || undefined) as IncidentStatus | undefined, q: q || undefined };
  const list = useQuery({ queryKey: ['incidents', query], queryFn: () => incidentsApi.list(query) });

  function apply(next: Record<string, string>) {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) p.set(k, v);
      else p.delete(k);
    }
    setParams(p);
  }

  return (
    <>
      <div className="row">
        <h1>Incidents</h1>
        <span className="grow" />
        {mode === 'participant' && (
          <Link className="button" to={`${basePath}/new`}>
            Report an incident
          </Link>
        )}
      </div>
      <p className="muted">
        {mode === 'cer'
          ? 'Fault reports submitted by participants against device models, units and installations. Status is controlled by CER; a recall can be raised from an incident.'
          : 'Faults your organisation has reported. You can add notes; CER updates the status.'}
      </p>
      <form
        className="row"
        onSubmit={(e) => {
          e.preventDefault();
          apply({ q: draft });
        }}
      >
        <input className="grow" placeholder="Search by title, id, serial or model number" value={draft} onChange={(e) => setDraft(e.target.value)} />
        <select value={status} onChange={(e) => apply({ status: e.target.value })} aria-label="Status filter">
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <button type="submit">Search</button>
      </form>
      <ProblemAlert error={list.error} />
      {list.data && <IncidentsTable items={list.data.items} basePath={basePath} showReporter={mode === 'cer'} />}
    </>
  );
}

function IncidentsTable({ items, basePath, showReporter }: { items: IncidentSummary[]; basePath: string; showReporter: boolean }) {
  if (items.length === 0) return <p className="muted">No incidents match.</p>;
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Incident</th>
          <th>Severity</th>
          <th>Status</th>
          <th>Model</th>
          <th>Unit / site</th>
          {showReporter && <th>Reported by</th>}
          <th>Raised</th>
        </tr>
      </thead>
      <tbody>
        {items.map((n) => (
          <tr key={n.id}>
            <td>
              <Link to={`${basePath}/${n.id}`}>{n.title}</Link>
              <br />
              <code className="muted">{n.id}</code>
            </td>
            <td>
              <StatusBadge status={n.severity} />
            </td>
            <td>
              <StatusBadge status={n.status} />
            </td>
            <td>
              {n.modelNumber} <span className="muted">{n.manufacturerName}</span>
            </td>
            <td>
              {n.unitSerial ?? <span className="muted">model-wide</span>}
              {n.installationNmi && <span className="muted"> · NMI {n.installationNmi}</span>}
            </td>
            {showReporter && <td>{n.reportedByParticipantName ?? 'CER'}</td>}
            <td>{formatDateTime(n.createdAt)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function NewIncidentPage({ basePath, devicesPath }: { basePath: string; devicesPath: string }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [values, setValues] = useState({
    unitId: params.get('unitId') ?? '',
    modelId: params.get('modelId') ?? '',
    installationId: params.get('installationId') ?? '',
    severity: 'medium' as IncidentSeverity,
    title: '',
    description: '',
    occurredOn: '',
    evidenceRef: '',
  });
  const raise = useMutation({
    mutationFn: () =>
      incidentsApi.raise({
        ...(values.unitId && { unitId: values.unitId.trim() }),
        ...(values.modelId && { modelId: values.modelId.trim() }),
        ...(values.installationId && { installationId: values.installationId.trim() }),
        severity: values.severity,
        title: values.title,
        description: values.description,
        ...(values.occurredOn && { occurredOn: values.occurredOn }),
        ...(values.evidenceRef && { evidenceRef: values.evidenceRef }),
      }),
    onSuccess: (n) => {
      void qc.invalidateQueries({ queryKey: ['incidents'] });
      navigate(`${basePath}/${n.id}`);
    },
  });
  const set = (k: keyof typeof values) => (e: { target: { value: string } }) => setValues((v) => ({ ...v, [k]: e.target.value }));

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    raise.mutate();
  }

  return (
    <>
      <p className="crumbs">
        <Link to={basePath}>Incidents</Link> / Report
      </p>
      <h1>Report an incident</h1>
      <p className="muted">
        Identify the device by unit, installation or model ID (copy it from <Link to={devicesPath}>My devices</Link>). You can only report against records your organisation is a party to: units you own or
        installed, models you manufacture, or approved catalogue models.
      </p>
      <form className="form card" onSubmit={onSubmit} noValidate>
        <Field label="Unit ID" hint="CER-D-…; the model is taken from the unit">
          <input value={values.unitId} onChange={set('unitId')} disabled={raise.isPending} />
        </Field>
        <Field label="Installation ID" hint="CER-I-…; optional, where the fault was observed">
          <input value={values.installationId} onChange={set('installationId')} disabled={raise.isPending} />
        </Field>
        <Field label="Model ID" hint="CER-M-…; use alone for a model-wide fault">
          <input value={values.modelId} onChange={set('modelId')} disabled={raise.isPending} />
        </Field>
        <Field label="Severity">
          <select value={values.severity} onChange={set('severity')} disabled={raise.isPending}>
            {SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Title">
          <input value={values.title} onChange={set('title')} required disabled={raise.isPending} />
        </Field>
        <Field label="Description" hint="What happened, how often, and any safety impact (at least 10 characters)">
          <textarea rows={5} value={values.description} onChange={set('description')} required disabled={raise.isPending} />
        </Field>
        <Field label="Date observed">
          <input type="date" value={values.occurredOn} onChange={set('occurredOn')} disabled={raise.isPending} />
        </Field>
        <Field label="Evidence reference" hint="Job sheet, photo reference or ticket number">
          <input value={values.evidenceRef} onChange={set('evidenceRef')} disabled={raise.isPending} />
        </Field>
        <ProblemAlert error={raise.error} />
        <div className="row">
          <button type="submit" disabled={raise.isPending}>
            Submit incident
          </button>
          <Link to={basePath}>Cancel</Link>
        </div>
      </form>
    </>
  );
}

export function IncidentDetailPage({ mode, basePath, devicesPath }: { mode: Mode; basePath: string; devicesPath: string }) {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const { user } = useAuth();
  const incident = useQuery({ queryKey: ['incidents', 'detail', id], queryFn: () => incidentsApi.get(id) });
  const [note, setNote] = useState('');
  const [nextStatus, setNextStatus] = useState<'acknowledged' | 'investigating' | 'resolved' | 'closed'>('acknowledged');
  const [statusNote, setStatusNote] = useState('');
  const onSaved = (n: Incident) => {
    qc.setQueryData(['incidents', 'detail', id], n);
    void qc.invalidateQueries({ queryKey: ['incidents'] });
  };
  const addNote = useMutation({
    mutationFn: () => incidentsApi.addNote(id, { note }),
    onSuccess: (n) => {
      setNote('');
      onSaved(n);
    },
  });
  const changeStatus = useMutation({
    mutationFn: () => incidentsApi.changeStatus(id, { status: nextStatus, note: statusNote }),
    onSuccess: (n) => {
      setStatusNote('');
      onSaved(n);
    },
  });

  if (incident.isLoading) return <p className="muted">Loading…</p>;
  if (incident.error || !incident.data) return <ProblemAlert error={incident.error ?? new Error('Not found')} />;
  const n = incident.data;
  const canManage = mode === 'cer' && isCerRole(user?.roles) && n.status !== 'closed';

  return (
    <>
      <p className="crumbs">
        <Link to={basePath}>Incidents</Link> / {n.id}
      </p>
      <div className="row">
        <h1>{n.title}</h1>
        <StatusBadge status={n.severity} />
        <StatusBadge status={n.status} />
      </div>
      <section className="card">
        <DefinitionList
          items={[
            ['Incident ID', <code>{n.id}</code>],
            ['Model', <Link to={`${devicesPath}/models/${n.modelId}`}>{n.modelNumber}</Link>],
            ['Manufacturer', mode === 'cer' ? <Link to={`/cer/participants/${n.manufacturerId}`}>{n.manufacturerName}</Link> : n.manufacturerName],
            ['Unit', n.unitId ? <Link to={`${devicesPath}/units/${n.unitId}`}>{n.unitSerial}</Link> : null],
            ['Installation', n.installationId ? <Link to={`${devicesPath}/installations/${n.installationId}`}>NMI {n.installationNmi}</Link> : null],
            ['Reported by', n.reportedByParticipantId ? `${n.reportedByParticipantName ?? n.reportedByParticipantId} (${n.reportedByUserName ?? 'user'})` : `CER (${n.reportedByUserName ?? 'user'})`],
            ['Date observed', n.occurredOn],
            ['Evidence', n.evidenceRef],
            ['Recall', n.recallId ? <Link to={`${basePath.replace(/\/incidents$/, '/recalls')}/${n.recallId}`}>{n.recallId}</Link> : mode === 'cer' && n.status !== 'closed' ? <Link to={`/cer/recalls/new?incidentId=${n.id}&modelId=${n.modelId}`}>Raise a recall from this incident</Link> : null],
            ['Raised', formatDateTime(n.createdAt)],
            ['Last updated', formatDateTime(n.updatedAt)],
          ]}
        />
        <h3>Description</h3>
        <p style={{ whiteSpace: 'pre-wrap' }}>{n.description}</p>
      </section>

      <h2>Timeline</h2>
      <table className="table">
        <thead>
          <tr>
            <th>When</th>
            <th>Event</th>
            <th>By</th>
            <th>Note</th>
          </tr>
        </thead>
        <tbody>
          {n.events.map((e) => (
            <tr key={e.id}>
              <td>{formatDateTime(e.createdAt)}</td>
              <td>{e.kind === 'status' ? `${e.fromStatus} → ${e.toStatus}` : e.kind}</td>
              <td>
                {e.actorName ?? '—'} {e.actorParticipantId ? '' : <span className="muted">(CER)</span>}
              </td>
              <td style={{ whiteSpace: 'pre-wrap' }}>{e.note}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {n.status !== 'closed' && (
        <form
          className="form card"
          onSubmit={(e) => {
            e.preventDefault();
            addNote.mutate();
          }}
        >
          <h2>Add a note</h2>
          <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} required disabled={addNote.isPending} />
          <ProblemAlert error={addNote.error} />
          <div>
            <button type="submit" disabled={addNote.isPending || note.trim().length === 0}>
              Add note
            </button>
          </div>
        </form>
      )}

      {canManage && (
        <form
          className="form card"
          onSubmit={(e) => {
            e.preventDefault();
            changeStatus.mutate();
          }}
        >
          <h2>Change status</h2>
          <Field label="New status">
            <select value={nextStatus} onChange={(e) => setNextStatus(e.target.value as typeof nextStatus)} disabled={changeStatus.isPending}>
              {(['acknowledged', 'investigating', 'resolved', 'closed'] as const).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Reason" hint="Recorded on the timeline and visible to the reporter">
            <textarea rows={2} value={statusNote} onChange={(e) => setStatusNote(e.target.value)} required disabled={changeStatus.isPending} />
          </Field>
          <ProblemAlert error={changeStatus.error} />
          <div>
            <button type="submit" disabled={changeStatus.isPending}>
              Update status
            </button>
          </div>
        </form>
      )}
    </>
  );
}
